import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js';
import type { StripeCardNumberElementChangeEvent } from '@stripe/stripe-js';
import { useClientTranslation } from '@/lib/core/i18n';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Icon } from '@/components/ui/icon';
import { Checkbox } from '@/components/ui/checkbox';
import { STRIPE_ELEMENT_STYLE } from '@/components/shared/profile/payment/stripe-provider';
import { StripeField, type CardField } from '@/components/shared/payment/card-fields';
import type { CardFieldState } from '../types';

interface NewCardPanelProps {
  holderName: string;
  onHolderNameChange: (name: string) => void;
  saveForFuture: boolean;
  onSaveForFutureChange: (save: boolean) => void;
  fieldState: CardFieldState;
  onFieldStateChange: (next: CardFieldState) => void;
  /** Per-field messages from Stripe, plus "required" once a blocked pay has been attempted. */
  errors: Partial<Record<CardField, string>>;
  onErrorChange: (field: CardField, message?: string) => void;
  /** Set after a blocked pay attempt so untouched required fields can be flagged. */
  submitAttempted: boolean;
}

/**
 * Inline card entry for the "Credit card" row (Figma: Checkout | Payment methods - credit card /
 * credit card filled, nodes 1716:93066 and 1716:93080).
 *
 * Uses the split Elements (number / expiry / cvc) rather than `PaymentElement` so the layout
 * matches the design exactly, and so the fields keep the project's `Input` chrome via
 * `StripeField`. The card data goes straight from these iframes to Stripe — it never reaches the
 * Amorim API.
 *
 * State lives in the parent because the CTA at the bottom of the screen has to know whether the
 * card is complete, and because collapsing the row must not silently discard what was typed.
 */
export function NewCardPanel({
  holderName,
  onHolderNameChange,
  saveForFuture,
  onSaveForFutureChange,
  fieldState,
  onFieldStateChange,
  errors,
  onErrorChange,
  submitAttempted,
}: NewCardPanelProps) {
  const t = useClientTranslation();
  const requiredMessage = t('App.Labels.Required');

  // Stripe's own message wins; after a blocked attempt an incomplete field is flagged as required so
  // the customer can see which one to finish rather than facing an inert button.
  const numberError =
    errors.number ?? (submitAttempted && !fieldState.complete.number ? requiredMessage : undefined);
  const expiryError =
    errors.expiry ?? (submitAttempted && !fieldState.complete.expiry ? requiredMessage : undefined);
  const cvcError = errors.cvc ?? (submitAttempted && !fieldState.complete.cvc ? requiredMessage : undefined);
  const nameError = submitAttempted && !holderName.trim() ? requiredMessage : undefined;

  function update(field: CardField, complete: boolean, empty: boolean) {
    onFieldStateChange({
      complete: { ...fieldState.complete, [field]: complete },
      // Tracked separately from `complete`: a half-typed number is neither complete nor invalid, but
      // it is still entered data, so the screen must treat the row as engaged.
      touched: { ...fieldState.touched, [field]: !empty },
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <FormField
        id="checkout-card-number"
        label={t('App.Labels.PaymentCardNumber')}
        required
        error={numberError}
      >
        <StripeField error={!!numberError}>
          <CardNumberElement
            options={{
              style: STRIPE_ELEMENT_STYLE,
              placeholder: t('App.Labels.PaymentCardNumberPlaceholder'),
            }}
            onChange={(event: StripeCardNumberElementChangeEvent) => {
              update('number', event.complete, event.empty);
              onErrorChange('number', event.error?.message);
            }}
          />
        </StripeField>
      </FormField>

      <div className="flex items-start gap-4">
        <FormField
          id="checkout-card-expiry"
          label={t('App.Labels.ExpirationDate')}
          required
          error={expiryError}
          className="min-w-0 flex-1"
        >
          <StripeField error={!!expiryError}>
            <CardExpiryElement
              options={{
                style: STRIPE_ELEMENT_STYLE,
                placeholder: t('App.Labels.ExpirationDatePlaceholder'),
              }}
              onChange={event => {
                update('expiry', event.complete, event.empty);
                onErrorChange('expiry', event.error?.message);
              }}
            />
          </StripeField>
        </FormField>

        <FormField
          id="checkout-card-cvc"
          label={t('App.Labels.Cvv')}
          required
          error={cvcError}
          className="min-w-0 flex-1"
        >
          <StripeField hasIcon error={!!cvcError}>
            <CardCvcElement
              options={{ style: STRIPE_ELEMENT_STYLE, placeholder: t('App.Labels.CvvPlaceholder') }}
              onChange={event => {
                update('cvc', event.complete, event.empty);
                onErrorChange('cvc', event.error?.message);
              }}
            />
            <div className="absolute right-3.75 flex items-center">
              <Icon
                name="help-circle"
                width={20}
                height={20}
                aria-label={t('App.Labels.CvvHelp')}
                className="text-jncquoi-neutral-1000 shrink-0"
              />
            </div>
          </StripeField>
        </FormField>
      </div>

      <FormField id="checkout-holder-name" label={t('App.Labels.NameOnCard')} required error={nameError}>
        <Input
          id="checkout-holder-name"
          value={holderName}
          placeholder={t('App.Labels.NameOnCardPlaceholder')}
          autoComplete="cc-name"
          error={!!nameError}
          onChange={event => onHolderNameChange(event.target.value)}
        />
      </FormField>

      <div className="flex items-start gap-3">
        <Checkbox
          id="checkout-save-card"
          checked={saveForFuture}
          onCheckedChange={checked => onSaveForFutureChange(checked === true)}
          className="mt-px"
        />
        <label htmlFor="checkout-save-card" className="flex flex-col gap-0.5">
          <span className="font-pp-neue-montreal text-jncquoi-neutral-1000 text-[12px] leading-[18px] tracking-[0.5px]">
            {t('App.Checkout.SaveDetails')}
          </span>
          <span className="font-pp-neue-montreal text-jncquoi-neutral-600 text-[11px] leading-[16px] tracking-[0.5px]">
            {t('App.Checkout.SaveDetailsHint')}
          </span>
        </label>
      </div>
    </div>
  );
}
